import React from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import api from '../../services/api';
import type { OfferPdfTemplateDefinition } from '../../utils/offerPdfTypes';

const API_LIST = '/offer-pdf-layouts';
const API_DETAIL = (id: number) => `/offer-pdf-layouts/${id}`;

export interface OfferPdfLayoutListItem {
  id: number;
  name: string;
  definition: OfferPdfTemplateDefinition;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useOfferPdfLayouts() {
  const { data, error, isLoading, mutate } = useSWR<OfferPdfLayoutListItem[]>(API_LIST, fetcher);
  return {
    layouts: data ?? [],
    isLoading,
    error,
    mutate,
  };
}

/** Hook for GenericListPage: returns { data, isLoading, mutate }. Optional searchParams filters by name client-side. */
export function useOfferPdfLayoutsList(searchParams?: string) {
  const { layouts, isLoading, mutate } = useOfferPdfLayouts();
  const data = React.useMemo(() => {
    if (!searchParams || !searchParams.trim()) return layouts;
    const term = searchParams.toLowerCase().trim();
    return layouts.filter((item) => item.name?.toLowerCase().includes(term));
  }, [layouts, searchParams]);
  return { data, isLoading, mutate };
}

export function useOfferPdfLayout(id: number | null) {
  const { data, error, isLoading, mutate } = useSWR<OfferPdfLayoutListItem | null>(
    id != null ? API_DETAIL(id) : null,
    fetcher
  );
  return {
    layout: data ?? null,
    isLoading,
    error,
    mutate,
  };
}

export async function createOfferPdfLayout(payload: { name: string; definition: OfferPdfTemplateDefinition; isDefault?: boolean }) {
  const res = await api.post(API_LIST, payload);
  return res.data as OfferPdfLayoutListItem;
}

export async function updateOfferPdfLayout(
  id: number,
  payload: { name?: string; definition?: OfferPdfTemplateDefinition; isDefault?: boolean }
) {
  const res = await api.patch(API_DETAIL(id), payload);
  return res.data as OfferPdfLayoutListItem;
}

export async function deleteOfferPdfLayout(id: number) {
  await api.delete(API_DETAIL(id));
}

export async function setDefaultOfferPdfLayout(id: number) {
  return updateOfferPdfLayout(id, { isDefault: true });
}
